import React from 'react';
import { getSignColor } from '../yearly-report/YearlyReport';
import {
  CommaFormattedString,
  Sign,
  SignAddedCommaFormattedPercentageString,
  SignAddedCommaFormattedString,
} from '../utils/formatter';

type AccountLinkageAsset = {
  totalAssetAmount: CommaFormattedString;
  assetAmountChangeSign: Sign;
  assetAmountChange: SignAddedCommaFormattedString;
  comparisonToLast: SignAddedCommaFormattedPercentageString;
};
type NoAccountLinkageAsset = {
  totalAssetAmount: '---';
  assetAmountChange: '---';
  comparisonToLast: '---';
};
export const isAccountLinkageAsset = (
  asset: AccountLinkageAsset | NoAccountLinkageAsset
): asset is AccountLinkageAsset => {
  return 'assetAmountChangeSign' in asset;
};
export type AssetChangeProps = {
  periodVerbose: string;
  updateInfoString: string | null;
  goPrevious: (() => void) | null;
  goNext: (() => void) | null;
  DateInformation: React.FC<Record<string, unknown>>;
  asset: AccountLinkageAsset | NoAccountLinkageAsset;
  assetFluctuationThresholdPer?: string | null;
  assetFluctuationThresholdPerSign?: Sign;
  isItemMask?: boolean;
};

export type UpdateInfoProps = {
  datetime: string | null;
};

type PagerProps = {
  DateInformation: React.FC<Record<string, unknown>>;
  goPrevious: (() => void) | null;
  goNext: (() => void) | null;
};

type AssetChangeInformationProps =
  | {
      totalAssetAmount: CommaFormattedString;
      assetAmountChange: SignAddedCommaFormattedString;
      assetAmountChangeSign: Sign;
      comparisonToLast: SignAddedCommaFormattedPercentageString;
      periodVerbose: string;
      assetFluctuationThresholdPer?: string | null;
      assetFluctuationThresholdPerSign?: Sign;
    }
  | {
      totalAssetAmount: '---';
      assetAmountChange: '---';
      comparisonToLast: '---';
      periodVerbose: string;
    };

export const UpdateInfo: React.FC<UpdateInfoProps> = ({ datetime }) => {
  return (
    <div
      className="flex justify-end items-center py-1 px-4 text-gray-700"
      style={{ fontSize: '13px' }}>
      <div className="flex items-center leading-4" style={{ height: '15px' }}>
        {datetime || '---'}
        <span className="ml-1">作成</span>
      </div>
    </div>
  );
};

const Pager: React.FC<PagerProps> = ({
  goNext,
  goPrevious,
  DateInformation,
}) => {
  return (
    <div className="flex items-center justify-between py-3 px-2">
      {goPrevious && (
        <div
          className="icon-chevron-left text-2xl text-bluegray-300 cursor-pointer"
          onClick={goPrevious}></div>
      )}
      <DateInformation />
      {goNext && (
        <div
          className="icon-chevron-right text-2xl text-bluegray-300 cursor-pointer"
          onClick={goNext}></div>
      )}
    </div>
  );
};

const AssetChangeInformation: React.FC<AssetChangeInformationProps> = props => {
  const {
    totalAssetAmount,
    assetAmountChange,
    comparisonToLast,
    periodVerbose,
  } = props;
  const assetAmountChangeSign =
    'assetAmountChangeSign' in props ? props.assetAmountChangeSign : '';
  const assetFluctuationThresholdPer =
    'assetFluctuationThresholdPer' in props
      ? props.assetFluctuationThresholdPer
      : null;
  const assetFluctuationThresholdPerSign =
    'assetFluctuationThresholdPerSign' in props
      ? props.assetFluctuationThresholdPerSign
      : null;

  const perColor =
    assetFluctuationThresholdPerSign !== null &&
    assetFluctuationThresholdPerSign !== undefined
      ? getSignColor(assetFluctuationThresholdPerSign)
      : '';

  const amountColor =
    assetAmountChangeSign === '' || getSignColor(assetAmountChangeSign);

  return (
    <div className="p-4 overflow-hidden">
      <div className="flex items-center justify-between pb-0.5 border-b border-bluegray-200">
        <div className="text-sm">資産総額</div>
        <div className="flex items-center text-lg">
          {totalAssetAmount}
          <div className="text-xs ml-0.5">円</div>
        </div>
      </div>
      <div className="mt-4 border-b border-bluegray-200">
        {assetFluctuationThresholdPer !== null &&
          assetFluctuationThresholdPer !== undefined && (
            <div className="flex items-center justify-between pb-0.5">
              <div className="text-sm font-bold text-bluegray-500">
                資産総額に
                <span className={`${perColor}`}>
                  <span className="ml-1 text-lg">
                    {assetFluctuationThresholdPer}%
                  </span>
                  以上
                </span>
                <span className="ml-1">の変動がありました</span>
              </div>
            </div>
          )}
        <div className="flex items-center justify-between mt-1 pb-0.5">
          <div className="text-lg font-bold">資産変動額</div>
          <div
            className={`flex items-center text-2xl ${amountColor}`}
            style={{ fontWeight: '600' }}>
            {assetAmountChange}
            <div className="text-xs ml-0.5">円</div>
          </div>
        </div>
        <div className="flex items-center justify-between pb-0.5">
          <div className="text-sm">前{periodVerbose}比</div>
          <div className={`flex items-center text-sm ${amountColor}`}>
            {comparisonToLast}
            <div className="text-xs ml-0.5">%</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AssetChange: React.FC<AssetChangeProps> = props => {
  const {
    periodVerbose,
    updateInfoString,
    asset,
    goNext,
    goPrevious,
    DateInformation,
    assetFluctuationThresholdPer,
    assetFluctuationThresholdPerSign,
    isItemMask: isItemMask_,
  } = props;
  const isItemMask = isItemMask_ !== undefined ? isItemMask_ : false;
  const assetChangeInformationProps: AssetChangeInformationProps = isAccountLinkageAsset(
    asset
  )
    ? {
        assetAmountChangeSign: asset.assetAmountChangeSign,
        assetAmountChange: asset.assetAmountChange,
        comparisonToLast: asset.comparisonToLast,
        periodVerbose,
        totalAssetAmount: asset.totalAssetAmount,
        assetFluctuationThresholdPer,
        assetFluctuationThresholdPerSign,
      }
    : {
        ...asset,
        periodVerbose,
      };
  return (
    <>
      <UpdateInfo datetime={updateInfoString} />
      <Pager
        DateInformation={DateInformation}
        goNext={goNext}
        goPrevious={goPrevious}
      />
      {!isItemMask && (
        <AssetChangeInformation {...assetChangeInformationProps} />
      )}
    </>
  );
};
